import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { apiFetch } from "../../components/helpers";

function useAnimatedCounter(
  target,
  duration = 1200
) {
  const [value, setValue] =
    useState(0);

  useEffect(() => {
    let frame,
      start = null;

    const step = (ts) => {
      if (!start) start = ts;

      const p = Math.min(
        (ts - start) / duration,
        1
      );

      setValue(
        Math.floor(
          (1 -
            Math.pow(
              1 - p,
              3
            )) *
            target
        )
      );

      if (p < 1)
        frame =
          requestAnimationFrame(
            step
          );
    };

    frame =
      requestAnimationFrame(
        step
      );

    return () =>
      cancelAnimationFrame(
        frame
      );
  }, [target, duration]);

  return value;
}

function Avatar({
  name,
  size = 32,
}) {
  const ini = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="d-flex align-items-center justify-content-center rounded-circle"
      style={{
        width: size,
        height: size,
        background: "#E1F5EE",
        color: "#085041",
        fontWeight: 700,
      }}
    >
      {ini}
    </div>
  );
}

function StatCard({
  icon,
  label,
  target,
  accent,
}) {
  const count =
    useAnimatedCounter(target);

  return (
    <div className="card border-0 shadow-sm h-100">
      <div
        className="card-body"
        style={{
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <div className="d-flex justify-content-between">
          <small>{label}</small>
          <span>{icon}</span>
        </div>

        <h3 className="fw-bold mt-2">
          {count}
        </h3>
      </div>
    </div>
  );
}

export default function AdminHome() {
  const [doctors, setDoctors] =
    useState([]);

  const [weeklyData, setWeeklyData] =
    useState([]);

  const [
    recentAppointments,
    setRecentAppointments,
  ] = useState([]);

  const fetchDoctors =
    async () => {
      try {
        const json =
          await apiFetch(
            `${process.env.REACT_APP_GET_ALL_DOCTOR}`
          );

        if (json.status) {
          setDoctors(
            json.data || []
          );
        } else {
          setDoctors([]);
        }
      } catch (e) {
        console.error(e);
        setDoctors([]);
      }
    };

  const fetchWeeklyAppointments =
    async () => {
      try {
        const json =
          await apiFetch(
            `${process.env.REACT_APP_GET_ALL_APPOINTMENTS}`
          );

        if (
          json.status &&
          json.data
        ) {
          const rows =
            json.data || [];

          const map = {
            Mon: 0,
            Tue: 0,
            Wed: 0,
            Thu: 0,
            Fri: 0,
            Sat: 0,
            Sun: 0,
          };

          rows.forEach(
            (item) => {
              const d =
                new Date(
                  item.appointment_date
                );

              const day =
                d.toLocaleDateString(
                  "en-US",
                  {
                    weekday:
                      "short",
                  }
                );

              if (
                map[
                  day
                ] !==
                undefined
              ) {
                map[
                  day
                ] += 1;
              }
            }
          );

          setWeeklyData([
            {
              day: "Mon",
              appointments:
                map.Mon,
            },
            {
              day: "Tue",
              appointments:
                map.Tue,
            },
            {
              day: "Wed",
              appointments:
                map.Wed,
            },
            {
              day: "Thu",
              appointments:
                map.Thu,
            },
            {
              day: "Fri",
              appointments:
                map.Fri,
            },
            {
              day: "Sat",
              appointments:
                map.Sat,
            },
            {
              day: "Sun",
              appointments:
                map.Sun,
            },
          ]);
        }
      } catch (e) {
        console.error(e);
      }
    };

  const fetchRecentAppointments =
    async () => {
      try {
        const json =
          await apiFetch(
            `${process.env.REACT_APP_RECENT_APPOINTMENTS}`
          );

        if (json.status) {
          setRecentAppointments(
            json.data || []
          );
        } else {
          setRecentAppointments([]);
        }
      } catch (e) {
        console.error(e);
        setRecentAppointments([]);
      }
    };

  useEffect(() => {
    fetchDoctors();
    fetchWeeklyAppointments();
    fetchRecentAppointments();
  }, []);

  const today =
    new Date().toLocaleDateString(
      "en-IN",
      {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between mb-4">
        <h4>
          OM Dental Dashboard
        </h4>
        <small>{today}</small>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <StatCard
            icon="🦷"
            label="Doctors"
            target={
              doctors.length
            }
            accent="#1D9E75"
          />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6>
                Weekly
                Appointments
              </h6>

              <ResponsiveContainer
                width="100%"
                height={250}
              >
                <BarChart
                  data={
                    weeklyData
                  }
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="appointments"
                    fill="#1D9E75"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="mb-3">
                Doctors on Duty
              </h6>

              {doctors.map(
                (doc) => (
                  <div
                    key={
                      doc.doctor_id
                    }
                    className="d-flex justify-content-between align-items-center mb-2 p-2 rounded"
                    style={{
                      background:
                        "#F8FAFC",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Avatar
                        name={
                          doc.name
                        }
                      />

                      <div>
                        <div className="fw-bold">
                          {
                            doc.name
                          }
                        </div>

                        <small className="text-muted">
                          {
                            doc.specialization
                          }
                        </small>
                      </div>
                    </div>

                    <span className="badge bg-success">
                      Active
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h6>
            Recent
            Appointments
          </h6>

          <table className="table">
            <tbody>
              {recentAppointments.map(
                (
                  a,
                  i
                ) => (
                  <tr
                    key={i}
                  >
                    <td>
                      {
                        a.patient
                      }
                    </td>
                    <td>
                      {
                        a.doctor
                      }
                    </td>
                    <td>
                      {
                        a.appointment_time
                      }
                    </td>
                    <td>
                      {
                        a.treatment
                      }
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          a.status ===
                          "Completed"
                            ? "bg-success"
                            : a.status ===
                              "Pending"
                            ? "bg-warning text-dark"
                            : "bg-danger"
                        }`}
                      >
                        {
                          a.status
                        }
                      </span>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}